import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import jsPDF from 'jspdf'; // Updated to default import
import html2canvas from 'html2canvas';
import './InvoicePreview.css';
import logoimage from "./assets/logo.gif";

const numberToWords = (num) => {
    const units = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
    const teens = ['ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
    const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

    const convertHundreds = (n) => {
        if (n === 0) return '';
        if (n < 10) return units[n];
        if (n < 20) return teens[n - 10];
        if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 === 0 ? '' : ' ' + units[n % 10]);
        return units[Math.floor(n / 100)] + ' hundred' + (n % 100 === 0 ? '' : ' ' + convertHundreds(n % 100));
    };

    const convertThousands = (n) => {
        if (n === 0) return '';
        if (n < 1000) return convertHundreds(n);
        if (n < 1000000) return convertHundreds(Math.floor(n / 1000)) + ' thousand' + (n % 1000 === 0 ? '' : ' ' + convertThousands(n % 1000));
        return convertHundreds(Math.floor(n / 1000000)) + ' million' + (n % 1000000 === 0 ? '' : ' ' + convertThousands(n % 1000000));
    };

    const amount = parseFloat(num) || 0;
    const dollars = Math.floor(amount);
    const cents = Math.round((amount - dollars) * 100);

    let result = convertThousands(dollars);
    if (cents > 0) {
        result += ' and ' + units[Math.floor(cents / 10)] + ' ' + units[cents % 10] + '/100';
    } else {
        result += ' ';
    }
    return result.charAt(0).toUpperCase() + result.slice(1) + ' USD only';
};

const InvoicePreview = () => {
    const { id } = useParams();
    const location = useLocation();
    const [invoiceData, setInvoiceData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const { formData = {} } = location.state || {};

    // Get current date in "June 04, 2025" format
    const currentDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
    });

    // Get due date (current date + 5 days)
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 5);
    const formattedDueDate = dueDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
    });

    useEffect(() => {
        const fetchInvoiceData = async () => {
            try {
                const response = await axios.get(`https://api.saadmotors.com/fetch_invoice.php?id=${id}`);
                if (response.data.error) {
                    throw new Error(response.data.error);
                }
                setInvoiceData(response.data);
                setError(null);
            } catch (err) {
                setError(err.message || 'Failed to load invoice data');
            } finally {
                setLoading(false);
            }
        };

        fetchInvoiceData();
    }, [id]);

    const generatePDF = async () => {
        setDownloading(true);
        try {
            const element = document.querySelector('.invoice-box');
            if (!element) {
                throw new Error('Invoice container not found');
            }

            // Create a new jsPDF instance
            const doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4',
            });

            // Options for html2canvas
            const options = {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                scrollY: 0
            };

            // Get the total height of the invoice content in pixels
            const totalHeight = element.scrollHeight;
            const pageHeightInPixels = 1123; // Approximate height of A4 at scale 2

            let position = 0;
            let pageNumber = 1;

            while (position < totalHeight) {
                // Capture the visible portion
                const canvas = await html2canvas(element, {
                    ...options,
                    windowHeight: pageHeightInPixels,
                    y: position,
                    height: Math.min(pageHeightInPixels, totalHeight - position)
                });

                const imgData = canvas.toDataURL('image/png');
                const imgWidth = doc.internal.pageSize.getWidth();
                const imgHeight = (canvas.height * imgWidth) / canvas.width;

                // Add image to current page
                if (pageNumber > 1) {
                    doc.addPage();
                }

                doc.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

                position += pageHeightInPixels;
                pageNumber++;
            }

            // Save the PDF to blob
            const pdfBlob = doc.output('blob');

            // Upload to server
            const formData = new FormData();
            formData.append('file', pdfBlob, `invoice_${id}.pdf`);
            formData.append('id', id);

            // Save the PDF to the database
            const response = await axios.post('https://api.saadmotors.com/save_invoice.php', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (response.data.error) {
                throw new Error(response.data.error);
            }

            // Trigger download
            const url = window.URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `invoice_${id}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            alert('Invoice generated, saved, and downloaded successfully!');
        } catch (err) {
            console.error('Error generating PDF:', err);
            alert('Failed to generate invoice: ' + err.message);
        } finally {
            setDownloading(false);
        }
    };

    if (loading) return <div>Loading preview...</div>;
    if (error) return <div>Error: {error}</div>;
    if (!invoiceData) return <div>No invoice data found for ID: {id}</div>;

    return (
        <div className="invoice-container">
            <div className="invoice-box">
                <div className="row invoice-header">
                    <div className='col-sm-2 logobox'>
                        <img src={logoimage} alt="Logo" className="invoice-logo" />
                    </div>
                    <div className='col-sm-7 titlebox'>
                        <h2>SAAD MOTORS (THAILAND) CO.,LTD</h2>
                        <p>127/2 Kaha Romklao Road, Rat Phatthana Subdistrict, Saphan Sung District, Bangkok 10240, Thailand</p>
                        <p>PHONE :+66-92-836-7345</p>
                        <p>Invoice for Vehicle ID: {id}</p>
                    </div>
                    <div className='col-sm-3 detailsbox'>
                        <h2>PROFORMA INVOICE</h2>
                    </div>
                </div>

                <div className="row invoice-section customer-consign-details">
                    <div className='col-sm-7'>
                        <h3>CUSTOMER DETAILS</h3>
                        <table className="invoice-table">
                            <tbody>
                                <tr>
                                    <td><strong>NAME:</strong></td>
                                    <td>{formData.client_name || 'N/A'}</td>
                                </tr>
                                <tr>
                                    <td><strong>ADDRESS:</strong></td>
                                    <td>{formData.client_address || 'N/A'}</td>
                                </tr>
                                <tr>
                                    <td><strong>CONTACT:</strong></td>
                                    <td>{formData.client_contact || 'N/A'}</td>
                                </tr>
                                <tr>
                                    <td><strong>EMAIL:</strong></td>
                                    <td>{formData.client_email || 'N/A'}</td>
                                </tr>
                            </tbody>
                        </table>
                        <h3>CONSIGNEE & NOTIFY DETAILS</h3>
                        <table className="invoice-table">
                            <tbody>
                                <tr>
                                    <td><strong>NAME:</strong></td>
                                    <td>{formData.consignee_name || 'N/A'}</td>
                                </tr>
                                <tr>
                                    <td><strong>ADDRESS:</strong></td>
                                    <td>{formData.consignee_address || 'N/A'}</td>
                                </tr>
                                <tr>
                                    <td><strong>CONTACT:</strong></td>
                                    <td>{formData.consignee_contact || 'N/A'}</td>
                                </tr>
                                <tr>
                                    <td><strong>EMAIL:</strong></td>
                                    <td>{formData.consignee_email || 'N/A'}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className='col-sm-5'>
                        <table className="invoice-table">
                            <tbody>
                                <tr>
                                    <td><strong>Invoice DATE:</strong></td>
                                    <td>{currentDate}</td>
                                </tr>
                                <tr>
                                    <td><strong>INVOICE #:</strong></td>
                                    <td>SN-{invoiceData.id}-{invoiceData.stock_id}</td>
                                </tr>
                                <tr>
                                    <td><strong>CUSTOMER #:</strong></td>
                                    <td>{invoiceData.client_id || 'N/A'}</td>
                                </tr>
                                <tr>
                                    <td><strong>AGENT:</strong></td>
                                    <td>{formData.agent || 'N/A'}</td>
                                </tr>
                                <tr>
                                    <td><strong>SHIPPING:</strong></td>
                                    <td>{formData.shipping || 'N/A'}</td>
                                </tr>
                                <tr>
                                    <td><strong>P.DEPARTURE:</strong></td>
                                    <td>{formData.departure || 'N/A'}</td>
                                </tr>
                                <tr>
                                    <td><strong>P.ARRIVAL:</strong></td>
                                    <td>{formData.arrival || 'N/A'}</td>
                                </tr>
                                <tr>
                                    <td><strong>COUNTRY:</strong></td>
                                    <td>{invoiceData.country || 'N/A'}</td>
                                </tr>
                                <tr>
                                    <td><strong>DOCUMENT:</strong></td>
                                    <td>{formData.document || 'N/A'}</td>
                                </tr>
                                <tr>
                                    <td><strong>INFOTERM:</strong></td>
                                    <td>{formData.infoterm || 'N/A'}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="bank-section">
                    <h3>BANKING DETAILS</h3>
                    <p>BENEFICIARY BANKING DETAILS (INTERNATIONAL WIRE TRANSFERS)</p>
                </div>

                <div className="bankdeatils-section">
                    <table className="invoice-table bank-table table table-striped">
                        <tbody>
                            <tr>
                                <td><strong>BANK NAME:</strong></td>
                                <td>KASIKORN BANK PUBLIC COMPANY</td>
                            </tr>
                            <tr>
                                <td><strong>BENEFICIARY NAME:</strong></td>
                                <td>SAAD MOTORS (THAILAND) CO.,LTD</td>
                            </tr>
                            <tr>
                                <td><strong>ACCOUNT NUMBER:</strong></td>
                                <td>236-8-38477-4</td>
                            </tr>
                            <tr>
                                <td><strong>BRANCH CODE:</strong></td>
                                <td>TESCO LOTUS CHAI 2 (RAMA 2 RD)</td>
                            </tr>
                            <tr>
                                <td><strong>BRANCH NAME:</strong></td>
                                <td>SAAD MOTORS CO,. LTD</td>
                            </tr>
                            <tr>
                                <td><strong>BRANCH ADDRESS:</strong></td>
                                <td>99/8 RAMA 2 RD. THA SAI MUANG SAMUT SAKHON 74000</td>
                            </tr>
                            <tr>
                                <td><strong>SWIFT CODE:</strong></td>
                                <td>KASITHBK</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="bankdeatils-section detailsamoutnt">
                    <table className="invoice-table bank-table table table-striped">
                        <thead>
                            <tr>
                                <th>QTY</th>
                                <th>DESCRIPTION OF GOODS</th>
                                <th>DEPOSIT 30%</th>
                                <th>BALANCE 70%</th>
                                <th>TOTAL CNF DEAL</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style={{ width: "15%" }}>1</td>
                                <td style={{ width: "35%" }}>
                                    <tr>
                                        <td><strong>Stock ID:</strong></td>
                                        <td>{invoiceData.stock_id || 'N/A'}</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Maker:</strong></td>
                                        <td>{invoiceData.maker || 'N/A'}</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Engine:</strong></td>
                                        <td>{invoiceData.engine || 'N/A'}</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Color:</strong></td>
                                        <td>{invoiceData.color || 'N/A'}</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Mileage:</strong></td>
                                        <td>{invoiceData.mileage || 'N/A'} km</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Body Type:</strong></td>
                                        <td>{invoiceData.body_type || 'N/A'}</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Chassis #:</strong></td>
                                        <td>{invoiceData.vin || 'N/A'}</td>
                                    </tr>
                                </td>
                                <td style={{ width: "15%" }}>USD 0</td>
                                <td style={{ width: "15%" }}>USD 0</td>
                                <td style={{ width: "15%" }}>${parseFloat(invoiceData.price || 0).toFixed(2)}</td>
                            </tr>
                            <tr>
                                <td colspan="2">
                                    Customer
                                </td>
                                <td></td>
                                <td></td>
                                <td></td>
                            </tr>
                            <tr>
                                <td colspan="2"><strong>GRAND TOTAL</strong></td>
                                <td><strong>USD 0</strong></td>
                                <td><strong>USD 0</strong></td>
                                <td><strong>${parseFloat(invoiceData.price || 0).toFixed(2)}</strong></td>
                            </tr>
                            <tr>
                                <td colSpan="5" style={{ textAlign: 'center', paddingTop: '20px', paddingBottom: "15px", fontSize: "12px", backgroundColor: "#110d96" }}>
                                    TOTAL AMOUNTS IN WORDS: {numberToWords(invoiceData.price || 0)}
                                </td>
                            </tr>
                            <tr>
                                <td colSpan="5" style={{ paddingTop: '15px', paddingBottom: "15px", backgroundColor: "#1d048b" }}>
                                    <div className='row lastdatebox'>
                                        <div className='col-sm-7'>
                                            <h4>WE/I CONFIRM AND AGREE THE DETAILS OF THE GOODS OF THIS</h4>
                                        </div>
                                        <div className='col-sm-5'>
                                            <h4>CUSTOMER ACKNOWLEDGEMENT</h4>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan="5" style={{ textAlign: 'center', paddingTop: '10px', color: 'red', backgroundColor: "#2a0493" }}>
                                    <strong>DUE DATE: {formattedDueDate}</strong>
                                </td>
                            </tr>
                            {/* <tr>
                                <td colSpan="5" style={{ padding: '20px', backgroundColor: '#FFFFFF' }}>
                                    <div className='terms-section' style={{ textAlign: 'left', fontSize: '12px' }}>
                                        <h3><strong>M.I TRADING CO., LTD JAPAN</strong></h3>
                                        <p><em>"This is an electronically generated invoice and does not require signature"</em></p>
                                        <p><strong>Terms and Conditions:</strong></p>
                                        <ol>
                                            <li>All Customer payments shall be made through Telegraphic Transfer (TT) or PayPal as according to the Country's prevailing Regulations.</li>
                                            <li>Customer shall state the Proforma Invoice No. as reference for payment in the information area of the TT/SWIFT application.</li>
                                            <li>The proof of payment shall be emailed by the Customer to M.I Trading Co., Ltd Japan, which would be verified and receipted on realization of funds.</li>
                                            <li>If the Deposit/Payment is not paid or Reservation Period of seven (07) working days or as per agreed Terms, the Exporter reserves the right to sell the vehicle to another customer.</li>
                                            <li>The Issuing & Correspondence Bank charges shall be paid by the Customer.</li>
                                            <li>The estimated shipment date would take place within three (03) weeks after confirmation of satisfactory receipt of Customer Payment Conditions.</li>
                                            <li>Any amendment request for BL, after shipment instruction, shall incur USD50 on each such adjustment, charged to the Customer.</li>
                                            <li>On settlement of the full amount, the Original Shipping Documents for Customs Clearance purpose shall be couriered/surrendered to the Customer: If given due date exceed Supplier has the right to forfeit the deal.</li>
                                            <li>Customer should settle timelously, and Customer shall not hold M.I Trading Co., Ltd Japan responsible for any delay, arising from the payment delays, nor for any penalties incurred therewith.</li>
                                            <li>Only Proof of Shipment will be provided after initial deposit, Copy of Export & Inspection Certificate will only be sent after 100% of the payment.</li>
                                            <li>Payments to be made seven (7) days before arrival of ship. If the full payment is not made seven (7) days prior to arrival of ship in the designated port, Supplier reserves the right to change consignee to their local agent without prior consent and will be charged a penalty to the original consignee.</li>
                                        </ol>
                                    </div>
                                </td>
                            </tr> */}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="invoice-actions mt-3">
                <button onClick={generatePDF} className="preview-btn" disabled={downloading}>
                    Generate Invoice
                </button>
            </div>

            {downloading && (
                <div className="download-overlay">
                    <div className="download-text">Wait till downloading the required PDF</div>
                </div>
            )}
        </div>
    );
};

export default InvoicePreview;